import { normalizeName } from '../shared/naming';
import type { JobRoleId, ProjectId } from '../shared/ids';
import type { MeasureBy } from './geometry';
import type { ItemFactor } from './own-factor';
import { pileLength, pileRateKey, pileTypeSteelTotal, pileVolume, type PileType } from './pile';
import { nomeDaPeca } from './planilha-por-peca';
import { serviceLockKeyOf } from './project-budget';
import { serviceKey } from './service-catalog';
import type { SteelGauge } from './steel';
import type { ItemKind } from './takeoff-line';

/**
 * A taxa de conversão média: quanto de cada item as obras já orçadas gastaram por unidade da
 * medida da planta (Fase 39, etapa 1 — D1 a D7, D11, D13; RN-496 a RN-502).
 *
 * ```text
 * R40 · CONCRETAGEM · CONCRETO FCK 35       1,0500 M³/m³ · 2 obras, 66 estacas
 * R40 · CORTE E DOBRA · CA 50 - 10 MM      36,6400 KG/estaca
 * ```
 *
 * A taxa é o índice da Fase 24 (RN-280) visto do outro lado: lá ele multiplica a medida e dá o
 * item; aqui o item e a medida já existem, nas peças que a planilha trouxe, e a divisão dá o
 * índice. A medida é **sempre a da planta** (D13): o volume da estaca é π·d²/4 × comprimento,
 * mesmo quando a planilha escreve outra coisa no serviço (a movimentação de terra em kg).
 *
 * > **As regras em uma linha:** só a peça da planilha com diâmetro no nome e comprimento na
 * > perfuração entra (RN-500); a média é por diâmetro, ponderada (RN-501); o item só entra se
 * > casa com um item da obra de referência (RN-498); o aço é kg por estaca (RN-501).
 *
 * Desde a etapa 2 entra também a estaca **desenhada**, com o número próprio do tipo dela — o
 * fator digitado e o aço digitado (RN-499). Ela vira linhas no mesmo formato da peça da
 * planilha (`drawnPileLines`), e a conta continua uma só (`averageRates`).
 *
 * Função pura sobre as linhas já lidas, como a produtividade (ADR-179): a leitura cruza as
 * obras no repositório, e a regra mora aqui, ao alcance do teste.
 */

// ── O diâmetro no nome ──────────────────────────────────────────────────────────

/**
 * O tipo no fim do nome: "E06-A - R40", "E80-A R60", "R-80". Dois ou três algarismos, em
 * centímetros: "R40" é Ø 40 cm. Só no **fim**, porque o começo do nome é a estaca (E06-A), e
 * um "R" no meio ("REFORÇO 40") não é tipo.
 */
const TIPO_NO_FIM_DO_NOME = /(?:^|[\s\-–—])R\s*-?\s*(\d{2,3})\s*$/i;

/** O diâmetro em metros que o nome da estaca escreve, ou `undefined` sem tipo no fim. */
export function diameterFromPieceName(name: string): number | undefined {
  const achado = TIPO_NO_FIM_DO_NOME.exec(name.trim());
  if (achado === null) return undefined;
  const centimetros = Number(achado[1]);
  return centimetros > 0 ? centimetros / 100 : undefined;
}

/** "R40", do diâmetro em metros — o rótulo que a tela e o nome da estaca usam. */
export function diameterLabel(diameter: number): string {
  return `R${String(Math.round(diameter * 100))}`;
}

// ── As linhas lidas ─────────────────────────────────────────────────────────────

/** O item de uma linha, como a obra o cadastrou. */
export interface RateAverageItem {
  readonly description: string;
  readonly code?: string;
  readonly unit?: string;
  readonly kind: ItemKind;
  readonly jobRoleId?: JobRoleId;
  readonly steelGauge?: SteelGauge;
}

/**
 * Uma linha da peça da planilha (`piece_quantities`) de uma obra da média.
 *
 * Sem `item`, é a quantidade do **serviço** na peça: dela sai o comprimento (a perfuração).
 * Com `item`, é o que a peça gastou daquele item no serviço — o numerador da taxa.
 */
export interface RateAveragePieceLine {
  readonly projectId: ProjectId;
  /**
   * Onde a peça mora na obra — a tipologia —, quando o repositório sabe. O nome só é único na
   * tipologia (`piece_quantities_uq`): a E01 do subsolo e a E01 de outro desenho são duas.
   */
  readonly placeId?: string;
  readonly pieceName: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  /** O "Mede por" do serviço: a grandeza da planta que divide o item. */
  readonly measureBy?: MeasureBy;
  readonly item?: RateAverageItem;
  readonly quantity: number;
  /**
   * A medida que a planta já deu à peça — a estaca **desenhada** (Fase 39, RN-499): o diâmetro
   * do tipo, o comprimento dela, o volume e o aço do tipo. Ausente na peça da planilha, que se
   * mede pelo nome e pela perfuração (RN-500).
   */
  readonly measures?: PieceMeasures;
}

/** Um item da obra de referência, no serviço dele (RN-498). */
export interface ReferenceItem extends RateAverageItem {
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly measureBy?: MeasureBy;
}

// ── O que a conta devolve ───────────────────────────────────────────────────────

/** A grandeza que divide o item: "m³", "m", "kg", "un" ou "estaca" (o aço por bitola). */
export type RateMeasureUnit = 'm' | 'm³' | 'kg' | 'un' | 'estaca';

export interface RateAverage {
  /** Metros: 0,40 na R40. */
  readonly diameter: number;
  /** A chave da trava do serviço (`serviceLockKeyOf`): a mesma em todas as obras. */
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  /** O item como a referência o escreve. */
  readonly item: ReferenceItem;
  readonly measureUnit: RateMeasureUnit;
  /** Σ insumo ÷ Σ medida (RN-501). */
  readonly rate: number;
  readonly quantity: number;
  readonly measure: number;
  /** De quantas obras e de quantas estacas veio. */
  readonly projects: number;
  readonly pieces: number;
}

export type OutOfAverageReason =
  /** O nome não termina no tipo ("E06-A", sem o R40). */
  | 'SEM_DIAMETRO'
  /** A peça não tem a quantidade do serviço que mede por comprimento (a perfuração). */
  | 'SEM_COMPRIMENTO'
  /** O item não casa com nenhum item da referência no mesmo serviço. */
  | 'ITEM_SEM_PAR'
  /** O serviço mede por uma grandeza que a peça da planilha não tem (marcadas, aço sem bitola). */
  | 'SEM_MEDIDA';

export const OUT_OF_AVERAGE_LABEL: Readonly<Record<OutOfAverageReason, string>> = {
  SEM_DIAMETRO: 'sem o tipo no fim do nome',
  SEM_COMPRIMENTO: 'sem a perfuração na peça',
  ITEM_SEM_PAR: 'item que a obra de referência não tem',
  SEM_MEDIDA: 'serviço sem medida na peça',
};

/**
 * O que ficou fora, agrupado para caber na tela: a peça sem diâmetro uma vez, e o item sem par
 * uma vez por obra × serviço × descrição, com quantas peças ele tinha.
 */
export interface OutOfAverage {
  readonly reason: OutOfAverageReason;
  readonly projectId: ProjectId;
  readonly pieceName?: string;
  readonly serviceName?: string;
  readonly description?: string;
  readonly pieces: number;
}

/** Quantas estacas e obras cada diâmetro teve. */
export interface DiameterSummary {
  readonly diameter: number;
  readonly pieces: number;
  readonly projects: number;
}

export interface RateAverages {
  readonly diameters: readonly DiameterSummary[];
  readonly averages: readonly RateAverage[];
  readonly outOfAverage: readonly OutOfAverage[];
}

// ── A medida da peça ────────────────────────────────────────────────────────────

/** O que a planta diz de uma peça da planilha (D11, RN-500). */
export interface PieceMeasures {
  readonly diameter: number;
  /** Metros: a quantidade da perfuração na peça. */
  readonly length: number;
  /** π·d²/4 × comprimento, em m³. */
  readonly volume: number;
  /** Os kg dos itens de aço do serviço que mede por aço, ou `undefined` sem aço. */
  readonly steel: number | undefined;
}

type MedidaDaPeca =
  | { readonly ok: true; readonly measures: PieceMeasures }
  | { readonly ok: false; readonly reason: 'SEM_DIAMETRO' | 'SEM_COMPRIMENTO' };

/**
 * A medida de uma peça a partir das linhas dela.
 *
 * O comprimento é a **maior** quantidade de serviço que mede por comprimento: na estaca é um só
 * (a perfuração), e o maior protege da linha repetida. O aço é a soma dos itens com bitola de
 * cada serviço que mede por aço, e vale o maior serviço: o aço só se compra no corte e dobra
 * (RN-366), e a planilha que o repetisse na montagem o contaria duas vezes.
 *
 * A estaca desenhada já traz a medida (`measures`, RN-499): o diâmetro é o do **tipo**, e não o
 * do nome — "E12" desenhada como R60 é R60, escreva o nome o que escrever.
 */
export function pieceMeasures(
  pieceName: string,
  lines: readonly Pick<
    RateAveragePieceLine,
    'serviceName' | 'measureBy' | 'item' | 'quantity' | 'measures'
  >[],
): MedidaDaPeca {
  const desenhada = lines.find((linha) => linha.measures !== undefined)?.measures;
  if (desenhada !== undefined) return { ok: true, measures: desenhada };

  const diameter = diameterFromPieceName(pieceName);
  if (diameter === undefined) return { ok: false, reason: 'SEM_DIAMETRO' };

  let length: number | undefined;
  const acoPorServico = new Map<string, number>();
  for (const linha of lines) {
    if (linha.item === undefined && linha.measureBy === 'LENGTH' && linha.quantity > 0) {
      length = Math.max(length ?? 0, linha.quantity);
    }
    if (linha.item?.steelGauge !== undefined && linha.measureBy === 'ACO') {
      const chave = normalizeName(linha.serviceName);
      acoPorServico.set(chave, (acoPorServico.get(chave) ?? 0) + linha.quantity);
    }
  }
  if (length === undefined) return { ok: false, reason: 'SEM_COMPRIMENTO' };

  const acos = [...acoPorServico.values()].filter((kg) => kg > 0);
  return {
    ok: true,
    measures: {
      diameter,
      length,
      volume: ((Math.PI * diameter ** 2) / 4) * length,
      steel: acos.length === 0 ? undefined : Math.max(...acos),
    },
  };
}

/**
 * Quanto da grandeza do serviço a peça tem — `undefined` quando a planta não a dá.
 *
 * O item de aço (com bitola) é a exceção: ele é kg **por estaca** (RN-501), e a medida dele é a
 * contagem. O tipo de estaca guarda o aço assim (`PileSteel.kgPerPile`), e é para lá que a média
 * vai na etapa 2.
 *
 * Só quatro grandezas a peça da planilha tem — comprimento, volume, contagem e aço. As outras
 * (as marcadas, a área, a face) a estaca sem desenho não dá, e o serviço fica sem medida.
 */
function medidaDoItem(
  measureBy: MeasureBy | undefined,
  item: Pick<RateAverageItem, 'steelGauge'>,
  medidas: PieceMeasures,
): { readonly value: number; readonly unit: RateMeasureUnit } | undefined {
  if (item.steelGauge !== undefined) return { value: 1, unit: 'estaca' };
  if (measureBy === 'LENGTH') return { value: medidas.length, unit: 'm' };
  if (measureBy === 'VOLUME') return { value: medidas.volume, unit: 'm³' };
  if (measureBy === 'COUNT') return { value: 1, unit: 'un' };
  if (measureBy === 'ACO' && medidas.steel !== undefined) {
    return { value: medidas.steel, unit: 'kg' };
  }
  return undefined;
}

// ── O par na referência ─────────────────────────────────────────────────────────

/**
 * O item da referência que corresponde a este, no mesmo serviço (RN-498).
 *
 * O código casa primeiro, sem diferenciar caixa (`e-685110` é `E-685110`); sem código de um dos
 * lados, casa a descrição normalizada. A mão de obra casa pela função quando as duas a têm, e
 * pela descrição quando não — "AJUDANTE" é o mesmo ajudante nas duas obras. É a mesma ideia da
 * chave da consolidação (`consolidationKey`), só que tolerante ao código que uma obra tem e a
 * outra não: a Montrezor sem código perderia o concreto que a Veneza tem com código.
 */
export function referenceItemFor(
  item: RateAverageItem,
  candidates: readonly ReferenceItem[],
): ReferenceItem | undefined {
  const descricao = normalizeName(item.description);
  if (item.kind === 'MAO_DE_OBRA') {
    return (
      (item.jobRoleId === undefined
        ? undefined
        : candidates.find(
            (ref) => ref.kind === 'MAO_DE_OBRA' && ref.jobRoleId === item.jobRoleId,
          )) ??
      candidates.find(
        (ref) => ref.kind === 'MAO_DE_OBRA' && normalizeName(ref.description) === descricao,
      )
    );
  }
  const codigo = item.code === undefined ? undefined : normalizeName(item.code);
  return (
    (codigo === undefined
      ? undefined
      : candidates.find(
          (ref) =>
            ref.kind !== 'MAO_DE_OBRA' &&
            ref.code !== undefined &&
            normalizeName(ref.code) === codigo,
        )) ??
    candidates.find(
      (ref) => ref.kind !== 'MAO_DE_OBRA' && normalizeName(ref.description) === descricao,
    )
  );
}

/** A identidade do item da referência dentro do serviço — a linha da média. */
function chaveDaReferencia(item: ReferenceItem): string {
  return item.kind === 'MAO_DE_OBRA'
    ? `mao:${item.jobRoleId ?? normalizeName(item.description)}`
    : `item:${item.code === undefined ? normalizeName(item.description) : normalizeName(item.code)}`;
}

// ── A conta ─────────────────────────────────────────────────────────────────────

interface Acumulado {
  readonly diameter: number;
  readonly serviceKey: string;
  readonly item: ReferenceItem;
  readonly unit: RateMeasureUnit;
  readonly ordem: number;
  quantity: number;
  measure: number;
  readonly projects: Set<ProjectId>;
  readonly pieces: Set<string>;
}

/**
 * A média de cada diâmetro × serviço × item, ponderada: Σ insumo ÷ Σ medida (RN-501).
 *
 * Sem referência não há média (RN-498): devolve tudo vazio, e quem chama diz por quê. A peça
 * sem diâmetro ou sem comprimento sai inteira (RN-500), e não só o item que precisaria da
 * medida: uma estaca sem perfuração é uma linha que a planilha deixou pela metade, e o aço dela
 * puxaria a média de uma estaca que não se sabe qual é. A linha com quantidade zero ou negativa
 * não entra: zero é a ferramenta que a planilha trouxe sem número, não "não gastou nada".
 */
export function averageRates(
  lines: readonly RateAveragePieceLine[],
  reference: readonly ReferenceItem[] | undefined,
): RateAverages {
  if (reference === undefined) return { diameters: [], averages: [], outOfAverage: [] };

  const daReferencia = new Map<string, ReferenceItem[]>();
  reference.forEach((item) => {
    const chave = serviceLockKeyOf(item.disciplineName, item.serviceName);
    daReferencia.set(chave, [...(daReferencia.get(chave) ?? []), item]);
  });
  const ordemDaReferencia = new Map(reference.map((item, indice) => [item, indice]));

  const pecas = new Map<string, RateAveragePieceLine[]>();
  for (const linha of lines) {
    const chave = `${linha.projectId}|${linha.placeId ?? ''}|${normalizeName(linha.pieceName)}`;
    pecas.set(chave, [...(pecas.get(chave) ?? []), linha]);
  }

  const acumulados = new Map<string, Acumulado>();
  const fora = new Map<string, OutOfAverage>();
  const porDiametro = new Map<number, { pieces: Set<string>; projects: Set<ProjectId> }>();

  const deixarFora = (chave: string, saida: Omit<OutOfAverage, 'pieces'>): void => {
    const antes = fora.get(chave);
    fora.set(chave, { ...saida, pieces: (antes?.pieces ?? 0) + 1 });
  };

  for (const [chaveDaPeca, linhas] of pecas) {
    const primeira = linhas[0]!;
    const medida = pieceMeasures(primeira.pieceName, linhas);
    if (!medida.ok) {
      deixarFora(`${medida.reason}|${chaveDaPeca}`, {
        reason: medida.reason,
        projectId: primeira.projectId,
        pieceName: primeira.pieceName,
      });
      continue;
    }
    const { diameter } = medida.measures;
    let entrou = false;
    const semMedidaNaPeca = new Set<string>();

    for (const linha of linhas) {
      if (linha.item === undefined || !(linha.quantity > 0)) continue;
      const servico = serviceLockKeyOf(linha.disciplineName, linha.serviceName);
      const par = referenceItemFor(linha.item, daReferencia.get(servico) ?? []);
      if (par === undefined) {
        deixarFora(
          `ITEM_SEM_PAR|${linha.projectId}|${servico}|${normalizeName(linha.item.description)}`,
          {
            reason: 'ITEM_SEM_PAR',
            projectId: linha.projectId,
            serviceName: linha.serviceName,
            description: linha.item.description,
          },
        );
        continue;
      }
      const medidaDoServico = medidaDoItem(linha.measureBy, par, medida.measures);
      if (medidaDoServico === undefined || !(medidaDoServico.value > 0)) {
        // Uma vez por peça × serviço: o serviço sem medida deixa todos os itens dele de fora.
        if (!semMedidaNaPeca.has(servico)) {
          semMedidaNaPeca.add(servico);
          deixarFora(`SEM_MEDIDA|${linha.projectId}|${servico}`, {
            reason: 'SEM_MEDIDA',
            projectId: linha.projectId,
            serviceName: linha.serviceName,
          });
        }
        continue;
      }

      const chave = `${String(diameter)}|${servico}|${chaveDaReferencia(par)}`;
      const acumulado = acumulados.get(chave) ?? {
        diameter,
        serviceKey: servico,
        item: par,
        unit: medidaDoServico.unit,
        ordem: ordemDaReferencia.get(par) ?? 0,
        quantity: 0,
        measure: 0,
        projects: new Set<ProjectId>(),
        pieces: new Set<string>(),
      };
      acumulado.quantity += linha.quantity;
      acumulado.measure += medidaDoServico.value;
      acumulado.projects.add(linha.projectId);
      acumulado.pieces.add(chaveDaPeca);
      acumulados.set(chave, acumulado);
      entrou = true;
    }

    if (entrou) {
      const resumo = porDiametro.get(diameter) ?? { pieces: new Set(), projects: new Set() };
      resumo.pieces.add(chaveDaPeca);
      resumo.projects.add(primeira.projectId);
      porDiametro.set(diameter, resumo);
    }
  }

  const averages = [...acumulados.values()]
    .sort((a, b) => a.diameter - b.diameter || a.ordem - b.ordem)
    .map((a): RateAverage => ({
      diameter: a.diameter,
      serviceKey: a.serviceKey,
      disciplineName: a.item.disciplineName,
      serviceName: a.item.serviceName,
      item: a.item,
      measureUnit: a.unit,
      rate: a.quantity / a.measure,
      quantity: a.quantity,
      measure: a.measure,
      projects: a.projects.size,
      pieces: a.pieces.size,
    }));

  const diameters = [...porDiametro.entries()]
    .sort(([a], [b]) => a - b)
    .map(([diameter, resumo]) => ({
      diameter,
      pieces: resumo.pieces.size,
      projects: resumo.projects.size,
    }));

  return { diameters, averages, outOfAverage: [...fora.values()] };
}

// ── A estaca desenhada (Fase 39, etapa 2 — RN-499) ──────────────────────────────

/**
 * Um item de um serviço que mede a estaca, como o catálogo de uma obra da média o tem.
 *
 * É onde o fator do tipo — guardado pela chave do serviço e a do item (`0088`) — acha a
 * descrição e o código que casam com a referência (RN-498).
 */
export interface RateAverageProjectItem {
  readonly projectId: ProjectId;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly measureBy?: MeasureBy;
  readonly item: RateAverageItem;
}

/** Uma estaca desenhada e posicionada de uma obra da média, com o tipo vivo dela. */
export interface RateAverageDrawnPile {
  readonly projectId: ProjectId;
  /** A tipologia do desenho: o nome da estaca só é único nela. */
  readonly unitTypeId: string;
  readonly name: string;
  readonly lengthOverride?: number;
  readonly type: Pick<PileType, 'diameter' | 'length' | 'steel' | 'rates'>;
  /** O fator próprio da estaca, por cima do tipo (Fase 39, RN-511). */
  readonly factors?: readonly ItemFactor[];
}

/** Uma peça que a planilha por peça trouxe, pelo nome, numa tipologia (RN-384). */
export interface RateAverageImportedPiece {
  readonly projectId: ProjectId;
  readonly unitTypeId: string;
  readonly pieceName: string;
}

/** O desenho das obras marcadas, como a média o lê (RN-499). */
export interface RateAverageDrawing {
  readonly piles: readonly RateAverageDrawnPile[];
  /** Os itens dos serviços da estaca de cada obra que tem estaca desenhada. */
  readonly items: readonly RateAverageProjectItem[];
  /** Os nomes da planilha por peça nas tipologias dessas estacas. */
  readonly importedPieces: readonly RateAverageImportedPiece[];
}

/** Nenhuma estaca desenhada: a obra que só tem a planilha, ou nenhuma obra marcada. */
export const NO_DRAWING: RateAverageDrawing = { piles: [], items: [], importedPieces: [] };

/**
 * O começo do `placeId` da estaca desenhada. A peça da planilha usa a tipologia pura; com o
 * prefixo, a E01 desenhada e a "E01 - R40" da planilha da mesma tipologia nunca viram uma peça só.
 */
export const DRAWN_PILE_PLACE = 'desenho:';

/** A medida da estaca desenhada: a que o desenho mede na obra (`pile.ts`), com o diâmetro do tipo. */
export function drawnPileMeasures(
  pile: Pick<RateAverageDrawnPile, 'lengthOverride' | 'type'>,
): PieceMeasures {
  const aco = pileTypeSteelTotal(pile.type);
  return {
    diameter: pile.type.diameter,
    length: pileLength(pile, pile.type),
    volume: pileVolume(pile, pile.type),
    steel: aco > 0 ? aco : undefined,
  };
}

/**
 * Quanto da grandeza do serviço a estaca desenhada tem — a medida que o fator multiplica.
 *
 * A marcada fica fora: o "Mede por" das marcadas conta as estacas marcadas da tipologia, e não
 * existe por estaca. As outras grandezas (área, face) a estaca não tem.
 */
function medidaDaDesenhada(
  measureBy: MeasureBy | undefined,
  medidas: PieceMeasures,
): number | undefined {
  if (measureBy === 'LENGTH') return medidas.length;
  if (measureBy === 'VOLUME') return medidas.volume;
  if (measureBy === 'COUNT') return 1;
  if (measureBy === 'ACO') return medidas.steel;
  return undefined;
}

/** O item que a chave do catálogo aponta numa obra, ou `null` quando duas linhas a disputam. */
type ItemDaChave = RateAverageProjectItem | null;

/**
 * Os itens de cada obra pela chave da composição — `pileRateKey(serviceKey, itemKey)`, a mesma
 * do catálogo que a bancada monta da leitura: o serviço pela disciplina e pelo nome (RN-164), o
 * item pela descrição normalizada (RN-251). O banco não deixa duas descrições iguais no mesmo
 * serviço da obra (`service_items_descricao_uq`); a chave repetida que sobra — duas disciplinas
 * de mesmo nome, uma da obra e uma global, com o mesmo serviço — não escolhe nenhuma das duas.
 */
function itensPelaChave(
  items: readonly RateAverageProjectItem[],
): ReadonlyMap<ProjectId, ReadonlyMap<string, ItemDaChave>> {
  const porObra = new Map<ProjectId, Map<string, ItemDaChave>>();
  for (const linha of items) {
    const daObra = porObra.get(linha.projectId) ?? new Map<string, ItemDaChave>();
    const chave = pileRateKey(
      serviceKey(linha.disciplineName, linha.serviceName),
      normalizeName(linha.item.description),
    );
    daObra.set(chave, daObra.has(chave) ? null : linha);
    porObra.set(linha.projectId, daObra);
  }
  return porObra;
}

/**
 * Os itens de aço de cada obra, pela bitola: os do serviço que mede por aço (o corte e dobra,
 * RN-366). É onde o aço do tipo — guardado por bitola, e não por item — vira item da obra.
 */
function acoPelaBitola(
  items: readonly RateAverageProjectItem[],
): ReadonlyMap<ProjectId, ReadonlyMap<SteelGauge, readonly RateAverageProjectItem[]>> {
  const porObra = new Map<ProjectId, Map<SteelGauge, RateAverageProjectItem[]>>();
  for (const linha of items) {
    const bitola = linha.item.steelGauge;
    if (bitola === undefined || linha.measureBy !== 'ACO') continue;
    const daObra = porObra.get(linha.projectId) ?? new Map<SteelGauge, RateAverageProjectItem[]>();
    daObra.set(bitola, [...(daObra.get(bitola) ?? []), linha]);
    porObra.set(linha.projectId, daObra);
  }
  return porObra;
}

function chaveDaImportada(projectId: ProjectId, unitTypeId: string, nome: string): string {
  return `${projectId}|${unitTypeId}|${nomeDaPeca(nome)}`;
}

/**
 * A estaca desenhada das obras marcadas, em linhas por peça — o formato da peça da planilha,
 * para `averageRates` continuar sendo a única conta (Fase 39, RN-499).
 *
 * **Só número próprio** (D5): o fator do tipo com origem `DIGITADO` dá o item (medida × fator),
 * e o aço por bitola digitado — ou sem origem, o de antes da Fase 39 — dá o kg por estaca. O
 * que segue a média fica fora, senão a média se realimentaria: o R40 que nasceu com 1,05 puxaria
 * a média seguinte para 1,05 sem ninguém ter decidido nada.
 *
 * A medida é a do desenho (D13): o comprimento corrigido ou o do tipo, o volume π·d²/4·L, a
 * contagem e o aço do tipo inteiro — esse, de qualquer origem, porque é a planta que o dá. A
 * marcada fica fora (`medidaDaDesenhada`). O fator de um item de aço também: o aço vem da
 * bitola do tipo (RN-378), e a conta da obra não o multiplica.
 *
 * A estaca desenhada com o nome de uma peça da planilha na mesma tipologia sai, como sai da
 * medida do desenho (RN-384): o número dela já vem pronto, nas linhas da planilha.
 */
export function drawnPileLines(drawing: RateAverageDrawing): RateAveragePieceLine[] {
  const itens = itensPelaChave(drawing.items);
  const aco = acoPelaBitola(drawing.items);
  const importadas = new Set(
    drawing.importedPieces.map((peca) =>
      chaveDaImportada(peca.projectId, peca.unitTypeId, peca.pieceName),
    ),
  );

  const linhas: RateAveragePieceLine[] = [];
  for (const estaca of drawing.piles) {
    if (importadas.has(chaveDaImportada(estaca.projectId, estaca.unitTypeId, estaca.name))) {
      continue;
    }
    const measures = drawnPileMeasures(estaca);
    const daPeca = (
      doCatalogo: RateAverageProjectItem,
      quantity: number,
    ): RateAveragePieceLine => ({
      projectId: estaca.projectId,
      placeId: `${DRAWN_PILE_PLACE}${estaca.unitTypeId}`,
      pieceName: estaca.name,
      disciplineName: doCatalogo.disciplineName,
      serviceName: doCatalogo.serviceName,
      ...(doCatalogo.measureBy === undefined ? {} : { measureBy: doCatalogo.measureBy }),
      item: doCatalogo.item,
      quantity,
      measures,
    });

    // O fator próprio da estaca (Fase 39, RN-511, RN-513) é número próprio sempre, e vence o do
    // tipo naquele item: a estaca que gasta mais concreto entra com o dela, as outras com o tipo.
    const proprios = new Map(
      (estaca.factors ?? []).map((proprio) => [
        pileRateKey(proprio.serviceKey, proprio.itemKey),
        proprio.factor,
      ]),
    );
    const doTipo = (estaca.type.rates ?? []).flatMap((fator) =>
      fator.origin === 'DIGITADO' && !proprios.has(pileRateKey(fator.serviceKey, fator.itemKey))
        ? [[pileRateKey(fator.serviceKey, fator.itemKey), fator.rate] as const]
        : [],
    );
    for (const [chave, fator] of [...proprios, ...doTipo]) {
      const doCatalogo = itens.get(estaca.projectId)?.get(chave);
      // Sem o item no catálogo (excluído depois), ou com a chave disputada: não se adivinha.
      if (doCatalogo === undefined || doCatalogo === null) continue;
      if (doCatalogo.item.steelGauge !== undefined) continue;
      const medida = medidaDaDesenhada(doCatalogo.measureBy, measures);
      if (medida === undefined || !(medida > 0)) continue;
      linhas.push(daPeca(doCatalogo, medida * fator));
    }

    for (const bitola of estaca.type.steel) {
      if (bitola.origin === 'MEDIA') continue;
      for (const doCatalogo of aco.get(estaca.projectId)?.get(bitola.gauge) ?? []) {
        linhas.push(daPeca(doCatalogo, bitola.kgPerPile));
      }
    }
  }
  return linhas;
}
